<template>
  <div class="alert transition-all duration-200">
    <div class="flex items-start justify-between">
      <div class="flex-1">
        <div class="flex items-start gap-2 mb-1 border-b border-b-grey-100 pb-2">
          <span class="notification-tile shrink-0" :class="`notification-tile--${notification.type}`">
            <span v-if="isRunning" class="pipeline-status-spinner" aria-hidden="true"></span>
            <SvgIcon v-else :name="icon" class="w-4 h-4" />
          </span>
          <div class="min-w-0 flex-1">
            <div class="flex items-center flex-wrap gap-2">
              <span class="font-semibold text-sm truncate">
                {{ notification.instanceCodename || notification.title }}
              </span>
              <span v-if="pillText" class="notification-pill shrink-0 truncate max-w-[45%]" :class="`notification-pill--${notification.type}`">
                {{ pillText }}
              </span>
            </div>
            <p class="notification-subline truncate">
              {{ notification.title }} · <span class="font-mono">{{ time }}</span>
            </p>
          </div>
          <button
              v-if="dismissible"
              type="button"
              class="shrink-0"
              :aria-label="$t('notifications.dismiss')"
              @click="$emit('remove', notification.id)"
          >
            <SvgIcon name="close" class="w-3 h-3" />
          </button>
        </div>
        <template v-if="timelineSteps">
          <NotificationTimeline :steps="timelineSteps" />
        </template>
        <template v-else-if="notification.deploySegments">
          <p class="text-xs">
            <span
                v-for="(segment, index) in notification.deploySegments"
                :key="segment.statusKey"
            >
              <span :class="deploySegmentClass(segment.statusKey)">{{ segment.label }}: {{ segment.names.join(', ') }}</span><span v-if="index < notification.deploySegments.length - 1">  ·  </span>
            </span>
          </p>
        </template>
        <p v-else class="text-xs text-gray-800">{{ notification.message }}</p>
        <div v-if="webUrl" class="flex justify-end mt-2">
          <a
              :href="webUrl"
              target="_blank"
              class="btn btn--alt btn--small text-xs"
          >
            {{ $t('notifications.view-pipeline') }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/store/Auth';
import SvgIcon from "@/components/SvgIcon.vue";
import NotificationTimeline from "@/components/NotificationTimeline.vue";
import { buildTimelineSteps, simplifyTimelineSteps } from '@/utils/pipelineTimeline';

const props = defineProps({
  notification: {
    type: Object,
    required: true
  },
  dismissible: {
    type: Boolean,
    default: false
  },
  showTimeline: {
    type: Boolean,
    default: false
  }
});

defineEmits(['remove']);

const { t } = useI18n();
const authStore = useAuthStore();

// Icons per GitLab pipeline status, grouped by what they visually mean to
// the user rather than one icon per raw status value. 'running' is handled
// separately below with a real spinner instead of a static icon.
const STATUS_ICONS = {
  created: 'clock',
  waiting_for_resource: 'clock',
  preparing: 'clock',
  pending: 'clock',
  scheduled: 'clock',
  manual: 'pause',
  success: 'check-circle',
  failed: 'alert-circle',
  canceled: 'ban',
  skipped: 'ban',
};

// Fallback for local, ephemeral notifications (immediate HTTP failures) which
// only ever carry a coarse `type`, not a pipeline `status`.
const TYPE_ICONS = {
  error: 'alert-circle',
  success: 'check-circle',
  pending: 'clock',
  info: 'alert-circle',
};

const isRunning = computed(() => props.notification.status === 'running');

const time = computed(() => props.notification.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));

// Customers get a jargon-free version - no raw GitLab statuses, no
// per-environment job names - same split as webUrl below (SPECTR-258).
const timelineSteps = computed(() => {
  if (!props.showTimeline) return null;
  const steps = buildTimelineSteps(props.notification);
  if (!steps) return null;
  return authStore.isAdmin ? steps : simplifyTimelineSteps(steps);
});

const icon = computed(() => {
  const { status, type } = props.notification;
  return STATUS_ICONS[status] || TYPE_ICONS[type] || 'alert-circle';
});

// Mirrors statusLabel()'s fallback in store/Notification.js.
const translateStatus = (rawStatus) => {
  const key = `pipeline-status.${rawStatus}`;
  const translated = t(key);
  return translated === key ? rawStatus : translated;
};

// The status pill text, shown next to the name - only pipeline-backed
// notifications have a real status to put there; local/ephemeral ones
// (immediate HTTP failures) don't, so they just skip the pill.
const pillText = computed(() => (
  props.notification.status ? translateStatus(props.notification.status) : null
));

// Successful deploys are the headline info here (e.g. "already deployed to
// updatetest") - bold + success color makes that jump out. Everything else
// (failed/manual/running) stays plain text now - too many colors made the
// segment row noisy to scan.
const DEPLOY_SEGMENT_CLASSES = {
  success: 'deploy-segment--success',
};

const deploySegmentClass = (statusKey) => DEPLOY_SEGMENT_CLASSES[statusKey] || '';

// The moodle pipeline is the one that actually ran and is the more useful
// link once available; the orchestration link is mainly a staff debugging
// aid for orchestration-stage failures. Neither is shown to non-staff users
// since customers generally have no GitLab access.
const webUrl = computed(() => {
  if (!authStore.isAdmin) return null;
  return props.notification.moodleWebUrl || props.notification.orchestrationWebUrl || null;
});
</script>

<style scoped>
.deploy-segment--success {
  color: var(--color-success);
  font-weight: 600;
}

.notification-tile {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 8px;
}

.notification-tile--error {
  color: var(--color-danger);
  background-color: var(--color-danger-bg);
}

.notification-tile--info {
  color: var(--color-info);
  background-color: var(--color-info-bg);
}

.notification-tile--success {
  color: var(--color-success);
  background-color: var(--color-grey-50);
}

.notification-tile--pending {
  color: var(--color-warning);
  background-color: var(--color-grey-50);
}

.notification-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.0625rem 0.5rem;
  border-radius: 50px;
  font-size: 0.7rem;
  font-weight: 600;
}

.notification-pill--error {
  color: var(--color-danger);
  background-color: var(--color-danger-bg);
}

.notification-pill--info {
  color: var(--color-info);
  background-color: var(--color-info-bg);
}

.notification-pill--success {
  color: var(--color-success);
  background-color: var(--color-grey-50);
}

.notification-pill--pending {
  color: var(--color-warning);
  background-color: var(--color-grey-50);
}

.notification-subline {
  margin-top: 0.125rem;
  font-size: 0.75rem;
  color: var(--color-grey-700);
}

.pipeline-status-spinner {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid var(--color-grey-100);
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: pipeline-status-spin 1s linear infinite;
}

@keyframes pipeline-status-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
